import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import SubscriptionCard from './SubscriptionCard';
import ChangePassword from './ChangePassword';
import ThemePreferences from './ThemePreferences';
import NotificationPreferences from './NotificationPreferences';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';

function Settings() {
  const { mode } = useThemeMode();
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({
    emailAlerts: true,
    pushNotifications: true
  });
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [assistantUsage, setAssistantUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState('');

  // Fetch notification preferences on mount
  useEffect(() => {
    fetchNotificationPreferences();
    fetchAssistantUsage();
  }, []);

  const fetchNotificationPreferences = async () => {
    try {
      const response = await dataAPI.getNotificationPreferences({ showLoading: false });
      if (response.status === 'success' && response.data) {
        setNotificationPrefs({
          emailAlerts: response.data.email_notifications_enabled,
          pushNotifications: response.data.push_notifications_enabled
        });
      }
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
    }
  };

  const fetchAssistantUsage = async () => {
    try {
      setUsageLoading(true);
      setUsageError('');
      const response = await dataAPI.getDashboard({ showLoading: false });
      if (response.status === 'success' && response.data) {
        setAssistantUsage(response.data.assistant_usage || null);
        if (!response.data.assistant_usage) {
          setUsageError('Usage details are unavailable.');
        }
      } else {
        setAssistantUsage(null);
        setUsageError('Usage details are unavailable.');
      }
    } catch (error) {
      console.error('Error fetching assistant usage:', error);
      setAssistantUsage(null);
      setUsageError('Unable to load usage details.');
    } finally {
      setUsageLoading(false);
    }
  };

  const handleNotificationPrefsChange = async (newPrefs) => {
    setNotificationPrefs(newPrefs);
    
    try {
      setNotificationLoading(true);
      await dataAPI.updateNotificationPreferences({
        email_notifications_enabled: newPrefs.emailAlerts,
        push_notifications_enabled: newPrefs.pushNotifications
      }, { showLoading: false });
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      // Revert on error
      fetchNotificationPreferences();
    } finally {
      setNotificationLoading(false);
    }
  };


  const containerStyle = {
    padding: '24px',
    backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
    minHeight: '100vh'
  };

  const cardsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: 24
  };

  const cardContainerStyle = {
    backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
    border: `1px solid ${mode === 'dark' ? '#333333' : '#e0e0e0'}`,
    borderRadius: '12px',
    padding: '20px',
    boxShadow: mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.30)' : '0 2px 8px rgba(0,0,0,0.06)'
  };

  const sectionTitleStyle = {
    fontSize: '22px',
    fontWeight: 800,
    color: mode === 'dark' ? '#FFFFFF' : '#2c3e50',
    margin: 0,
    paddingBottom: '12px',
    borderBottom: `1px solid ${mode === 'dark' ? '#1f1f1f' : '#efefef'}`,
  };

  const hasUsageData = !!assistantUsage && !usageError;

  const formatTokenValue = (value) => {
    if (value === null || value === undefined) return '--';
    if (typeof value === 'number') {
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    return value;
  };

  const remainingTokensRaw = assistantUsage?.remaining_tokens ?? null;
  const usedTokensRaw = assistantUsage?.used_tokens ?? null;

  const remainingTokens = formatTokenValue(remainingTokensRaw);
  const usedTokens = formatTokenValue(usedTokensRaw);

  // Responsive wrapper to align like the provided mock
  return (
    <div style={containerStyle}>
      <div style={cardsGridStyle}>
        <div style={{ ...cardContainerStyle, minHeight: '220px' }}>
          <h2 style={sectionTitleStyle}>Subscription</h2>
          <SubscriptionCard />
        </div>
        <div style={{ ...cardContainerStyle, minHeight: '220px', position: 'relative' }}>
          <h2 style={sectionTitleStyle}>Password</h2>
          <ChangePassword onLoadingChange={setPasswordLoading} />
          
          {/* Password Loading Overlay */}
          {passwordLoading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1003,
              backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(4px)',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              overflow: 'hidden'
            }}>
              {/* Fill Animation Loading Indicator */}
              <div className="fill-animation-container" style={{
                transform: 'scale(0.8)',
                transformOrigin: 'center'
              }}>
                {/* Background container */}
                <div className="fill-animation-background" />
                
                {/* Progress fill animation */}
                <div className="fill-animation-progress" />
                
                {/* AppIconBlue with pulse animation */}
                <img 
                  src={AppIconBlue} 
                  alt="Loading" 
                  className={`fill-animation-icon pulse-animation-icon`}
                />
              </div>
            </div>
          )}
        </div>
        <div style={{ ...cardContainerStyle }}>
          <h2 style={sectionTitleStyle}>Theme</h2>
          <ThemePreferences />
        </div>
        <div style={{ ...cardContainerStyle, position: 'relative' }}>
          <h2 style={sectionTitleStyle}>Notification Preferences</h2>
          <NotificationPreferences 
            values={notificationPrefs} 
            onChange={handleNotificationPrefsChange} 
          />
          
          {/* Notification Loading Overlay */}
          {notificationLoading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1003,
              backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(4px)',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              overflow: 'hidden'
            }}>
              {/* Fill Animation Loading Indicator */}
              <div className="fill-animation-container" style={{
                transform: 'scale(0.8)',
                transformOrigin: 'center'
              }}>
                {/* Background container */}
                <div className="fill-animation-background" />
                
                {/* Progress fill animation */}
                <div className="fill-animation-progress" />
                
                {/* AppIconBlue with pulse animation */}
                <img 
                  src={AppIconBlue} 
                  alt="Loading" 
                  className={`fill-animation-icon pulse-animation-icon`}
                />
              </div>
            </div>
          )}
        </div>
        <div style={{ ...cardContainerStyle }}>
          <h2 style={sectionTitleStyle}>AI Assistant Usage</h2>
          {usageLoading ? (
            <div style={{
              paddingTop: '16px',
              fontFamily: 'Urbanist, Arial, sans-serif',
              color: mode === 'dark' ? '#FFFFFF' : '#2c3e50'
            }}>
              Loading usage details...
            </div>
          ) : usageError && !assistantUsage ? (
            <div style={{
              paddingTop: '16px',
              fontFamily: 'Urbanist, Arial, sans-serif',
              color: mode === 'dark' ? '#f87171' : '#c0392b'
            }}>
              {usageError}
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              paddingTop: '16px',
              fontFamily: 'Urbanist, Arial, sans-serif',
              color: mode === 'dark' ? '#FFFFFF' : '#2c3e50'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Used Tokens</span>
                <span style={{ fontWeight: 700, color: mode === 'dark' ? '#ff7675' : '#e74c3c' }}>
                  {hasUsageData ? usedTokens : '--'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Remaining Tokens</span>
                <span style={{ fontWeight: 700, color: '#26C3C0' }}>
                  {hasUsageData ? remainingTokens : '--'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;


